/**
 * Подарки: ссылка, список купленных и отправка другу.
 *
 * Отправка — готовым сообщением бота (картинка, подпись, кнопка «Получить
 * подарок»): сервер сохраняет его через savePreparedInlineMessage, а здесь
 * shareMessage открывает выбор чата. Старые клиенты Telegram этого не умеют —
 * для них обычный выбор чата со ссылкой.
 */
import { shareMessage } from '@telegram-apps/sdk-react';
import { useQuery } from '@tanstack/react-query';

import { apiGet, apiPost } from '@/shared/api/client';
import { giftShareSchema, giftsSchema } from '@/shared/api/schemas';
import { config } from '@/shared/config/env';
import { isGuest } from '@/shared/lib/guest';
import { shareLink } from '@/shared/lib/links';

export function giftLink(code: string): string {
  return `https://t.me/${config.botUsername}?start=gift_${code}`;
}

export function useGifts() {
  return useQuery({
    queryKey: ['gifts'],
    queryFn: () => apiGet('/gifts', giftsSchema),
    // Гостю (браузер без Telegram) подарков быть не может — запрос не нужен.
    enabled: !isGuest(),
  });
}

/** Отправить подарок другу. false — отправить не вышло, пусть копируют ссылку. */
export async function sendGift(gift: { id: string; code: string }, text: string): Promise<boolean> {
  if (shareMessage.isAvailable()) {
    let preparedId: string | null = null;
    try {
      preparedId = (await apiPost(`/gifts/${gift.id}/share`, {}, giftShareSchema))
        .prepared_message_id;
    } catch {
      // Сервер не подготовил сообщение — ниже запасной путь со ссылкой.
    }
    if (preparedId) {
      try {
        await shareMessage(preparedId);
      } catch {
        // Человек закрыл выбор чата — это не ошибка, второй раз не спрашиваем.
      }
      return true;
    }
  }
  return shareLink(giftLink(gift.code), text);
}

export async function copyText(value: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(value);
    return true;
  } catch {
    // Старые WebView без Clipboard API — через временное поле.
    const field = document.createElement('textarea');
    field.value = value;
    document.body.appendChild(field);
    field.select();
    const ok = document.execCommand('copy');
    field.remove();
    return ok;
  }
}
